import React, { useMemo, useState } from 'react'
import useSWR from 'swr'
import { FiEdit, FiEye, FiTrash2 } from 'react-icons/fi'
import Spinner from '../Spinner'
import { Tooltip } from '../../layout/sidebar'
import { ErrorNotification, SuccessNotification } from '../../utils'
import api, { fetcher } from '../../utils/api'
import RoleFormModal from './RoleFormModal'
import RoleDetailsModal from './RoleDetailsModal'
import ConfirmActionModal from './ConfirmActionModal'

const PermissionsTab = ({ createModalOpen, setCreateModalOpen }) => {
  const [editingRole, setEditingRole] = useState(null)
  const [viewingRole, setViewingRole] = useState(null)
  const [roleToDelete, setRoleToDelete] = useState(null)
  const [deletingRole, setDeletingRole] = useState(false)

  const {
    data: permissionsData,
    error: permissionsError,
    isLoading: loadingPermissions
  } = useSWR('permissions/', fetcher)

  const {
    data: rolesData,
    error: rolesError,
    isLoading: loadingRoles,
    mutate: mutateRoles
  } = useSWR('roles/', fetcher)

  const permissions = useMemo(
    () => (Array.isArray(permissionsData) ? permissionsData : permissionsData?.results || []),
    [permissionsData]
  )

  const roles = useMemo(
    () => (Array.isArray(rolesData) ? rolesData : rolesData?.results || []),
    [rolesData]
  )

  const handleDeleteRole = async () => {
    if (!roleToDelete) return

    setDeletingRole(true)
    try {
      await api.delete(`roles/${roleToDelete.id}/`)
      SuccessNotification('Role deleted successfully.')
      mutateRoles()
      setRoleToDelete(null)
    } catch (error) {
      ErrorNotification(error.response?.data?.detail || error.response?.data || error.message || 'Failed to delete role.')
    } finally {
      setDeletingRole(false)
    }
  }

  if (loadingPermissions || loadingRoles) {
    return (
      <div className="h-96 grid items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (permissionsError || rolesError) {
    return (
      <div className="text-center py-12 text-red-500 dark:text-red-400">
        Failed to load permissions data. Please try again.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="border-b pb-3 border-gray-200 dark:border-border-dark w-full">
        <h1 className="text-base font-bold dark:text-white">Permissions</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Create and manage permission roles, then assign these roles when inviting team members.
        </p>
      </div>

      <div className="w-full">
        <div className="flex flex-col">
          <div className="-m-1.5 overflow-x-auto">
            <div className="p-1.5 min-w-full inline-block align-middle">
              <div className="bg-white dark:bg-dark border dark:border-border-dark rounded-lg shadow-sm overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-border-dark">
                  <thead className="bg-transparent dark:bg-secondaryDark">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 whitespace-nowrap">Role Name</th>
                      <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 whitespace-nowrap">Description</th>
                      <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 whitespace-nowrap">Permissions</th>
                      <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 whitespace-nowrap">Status</th>
                      <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 whitespace-nowrap">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:divide-border-dark">
                    {roles.length > 0 ? (
                      roles.map((role) => (
                        <tr key={role.id} className="text-[13px] hover:bg-gray-50 dark:hover:bg-secondaryDark">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800 dark:text-neutral-200">{role.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 dark:text-white min-w-[180px]">{role.description || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {Array.isArray(role.permissions) ? `${role.permissions.length} assigned` : '0 assigned'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${role.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
                              {role.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex gap-2">
                              <Tooltip text="Edit Role">
                                <button
                                  onClick={() => setEditingRole(role)}
                                  className="text-gray-400 hover:text-yellowcolor"
                                >
                                  <FiEdit size={16} />
                                </button>
                              </Tooltip>
                              <Tooltip text="View Role Details">
                                <button
                                  onClick={() => setViewingRole(role)}
                                  className="text-gray-400 hover:text-blue-500"
                                >
                                  <FiEye size={16} />
                                </button>
                              </Tooltip>
                              <Tooltip text="Delete Role">
                                <button
                                  onClick={() => setRoleToDelete(role)}
                                  className="text-gray-400 hover:text-red-500"
                                >
                                  <FiTrash2 size={16} />
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                          No permission roles found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <RoleFormModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        permissions={permissions}
        onSuccess={mutateRoles}
      />

      <RoleFormModal
        isOpen={Boolean(editingRole)}
        onClose={() => setEditingRole(null)}
        mode="edit"
        role={editingRole}
        permissions={permissions}
        onSuccess={mutateRoles}
      />

      <RoleDetailsModal
        isOpen={Boolean(viewingRole)}
        role={viewingRole}
        onClose={() => setViewingRole(null)}
        onEdit={() => {
          setEditingRole(viewingRole)
          setViewingRole(null)
        }}
      />

      <ConfirmActionModal
        isOpen={Boolean(roleToDelete)}
        onClose={() => {
          if (!deletingRole) setRoleToDelete(null)
        }}
        onConfirm={handleDeleteRole}
        loading={deletingRole}
        title="Delete Permission Role"
        message={`Are you sure you want to delete "${roleToDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        icon={FiTrash2}
      />
    </div>
  )
}

export default PermissionsTab
